import React, { useCallback, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { dt } from "../../themes/redesignTokens";
import { GalleryImage } from "./ImageGalleryTypes";
import UploadImageCrop from "../UploadImageCrop";
import useTranslation from "../../hooks/useTranslation";

export interface MultiImageUploadProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
  maxImages?: number;
  maxFileSizeMb?: number;
  enableCrop?: boolean;
  cropAspectRatio?: number;
  cropVariant?: "circle" | "square" | "rectangle";
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
}

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ACCEPTED_EXTENSIONS = /\.(jpg|jpeg|png|webp)$/i;

const isValidImageType = (file: File): boolean => {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  if (file.name && ACCEPTED_EXTENSIONS.test(file.name)) return true;
  return false;
};

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (err) => reject(err);
  });
};

const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  images,
  onChange,
  maxImages = 10,
  maxFileSizeMb = 5,
  enableCrop = false,
  cropAspectRatio = 1,
  cropVariant = "square",
  placeholder,
  disabled = false,
  compact = false,
}) => {
  const { translate } = useTranslation("common");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag-and-drop zone state
  const [isDragOver, setIsDragOver] = useState(false);

  // Drag-to-reorder state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Error state
  const [errors, setErrors] = useState<string[]>([]);

  // Crop state
  const [cropImageIndex, setCropImageIndex] = useState<number | null>(null);

  // --- File processing ---

  const processFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      const newErrors: string[] = [];
      const newImages: GalleryImage[] = [];

      // Check total count
      const remainingSlots = maxImages - images.length;
      if (remainingSlots <= 0) {
        newErrors.push(
          translate("gallery.upload.tooManyFiles", { max: maxImages })
        );
        setErrors(newErrors);
        return;
      }

      const filesToProcess = files.slice(0, remainingSlots);
      if (files.length > remainingSlots) {
        newErrors.push(
          translate("gallery.upload.tooManyFiles", { max: maxImages })
        );
      }

      for (const file of filesToProcess) {
        // Validate type
        if (!isValidImageType(file)) {
          newErrors.push(
            translate("gallery.upload.invalidFormat", { name: file.name })
          );
          continue;
        }

        // Validate size
        if (file.size > maxFileSizeMb * 1024 * 1024) {
          newErrors.push(
            translate("gallery.upload.fileTooLarge", {
              name: file.name,
              size: maxFileSizeMb,
            })
          );
          continue;
        }

        try {
          const base64 = await readFileAsBase64(file);
          newImages.push({
            id: crypto.randomUUID(),
            src: base64,
            name: file.name,
            size: file.size,
            isNew: true,
            isPrimary: images.length === 0 && newImages.length === 0, // first image is primary by default
          });
        } catch {
          newErrors.push(
            translate("gallery.upload.invalidFormat", { name: file.name })
          );
        }
      }

      setErrors(newErrors);

      if (newImages.length > 0) {
        onChange([...images, ...newImages]);
      }
    },
    [images, maxImages, maxFileSizeMb, onChange, translate]
  );

  // --- Drop zone handlers ---

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (!disabled && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [disabled, processFiles]
  );

  const handleBrowseClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
      // Reset input so same file can be re-selected
      e.target.value = "";
    },
    [processFiles]
  );

  // --- Remove ---

  const handleRemove = useCallback(
    (index: number) => {
      const updated = images.filter((_, i) => i !== index);
      // If removed image was primary, set the first remaining as primary
      const removedWasPrimary = images[index]?.isPrimary;
      if (removedWasPrimary && updated.length > 0) {
        updated[0] = { ...updated[0], isPrimary: true };
      }
      onChange(updated);
    },
    [images, onChange]
  );

  // --- Primary toggle ---

  const handleTogglePrimary = useCallback(
    (index: number) => {
      const updated = images.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      }));
      onChange(updated);
    },
    [images, onChange]
  );

  // --- Drag-to-reorder ---

  const handleReorderDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      e.dataTransfer.effectAllowed = "move";
      // Set minimal drag data to satisfy Firefox
      e.dataTransfer.setData("text/plain", String(index));
      setDragIndex(index);
    },
    []
  );

  const handleReorderDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverIndex(index);
    },
    []
  );

  const handleReorderDrop = useCallback(
    (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === targetIndex) {
        setDragIndex(null);
        setDragOverIndex(null);
        return;
      }

      const updated = [...images];
      const [dragged] = updated.splice(dragIndex, 1);
      updated.splice(targetIndex, 0, dragged);
      onChange(updated);
      setDragIndex(null);
      setDragOverIndex(null);
    },
    [dragIndex, images, onChange]
  );

  const handleReorderDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  // --- Crop ---

  const handleThumbnailClick = useCallback(
    (index: number) => {
      if (enableCrop) {
        setCropImageIndex(index);
      }
    },
    [enableCrop]
  );

  const handleCropSave = useCallback(
    (file: { name?: string; content?: string } | null) => {
      if (cropImageIndex !== null && file?.content) {
        const updated = [...images];
        updated[cropImageIndex] = {
          ...updated[cropImageIndex],
          src: file.content,
        };
        onChange(updated);
      }
      setCropImageIndex(null);
    },
    [cropImageIndex, images, onChange]
  );

  const handleCropClose = useCallback(() => {
    setCropImageIndex(null);
  }, []);

  // --- Sizing ---
  const thumbnailSize = compact ? 60 : 80;
  const dropZoneHeight = compact ? 80 : 120;

  const hasErrors = errors.length > 0;
  const canAddMore = images.length < maxImages;

  return (
    <Box
      sx={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        fontFamily: dt.font,
      }}
    >
      {/* Drop zone */}
      {canAddMore && (
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            height: `${dropZoneHeight}px`,
            border: `2px dashed ${
              hasErrors
                ? "#C0392B"
                : isDragOver
                ? dt.blue
                : dt.cardBorder
            }`,
            borderRadius: dt.radiusSm,
            backgroundColor: isDragOver ? dt.blueGlow : dt.inputBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: compact ? "4px" : "6px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: dt.blue,
              backgroundColor: dt.blueSoft,
            },
          }}
          onClick={handleBrowseClick}
          role="button"
          aria-label={translate("gallery.upload.dropZone")}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleBrowseClick();
            }
          }}
        >
          <CloudUploadOutlinedIcon
            sx={{
              fontSize: compact ? 24 : 32,
              color: isDragOver ? dt.blue : dt.textMuted,
              transition: "color 0.2s ease",
            }}
          />
          <Box
            sx={{
              fontSize: compact ? "12px" : "13px",
              color: dt.textSecondary,
              fontFamily: dt.font,
            }}
          >
            {placeholder || translate("gallery.upload.dropZone")}
          </Box>
          <Box
            sx={{
              fontSize: "11px",
              color: dt.textMuted,
              fontFamily: dt.font,
            }}
          >
            {translate("gallery.upload.supportedFormats")}
            {" \u00B7 "}
            {translate("gallery.upload.maxFileSize", { size: maxFileSizeMb })}
          </Box>
        </Box>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/jpg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      {/* Error messages */}
      {hasErrors && (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
          {errors.map((err, i) => (
            <Box
              key={i}
              sx={{
                fontSize: "12px",
                color: "#C0392B",
                fontFamily: dt.font,
                fontWeight: 500,
              }}
            >
              {err}
            </Box>
          ))}
        </Box>
      )}

      {/* Thumbnail grid */}
      {images.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${thumbnailSize}px, 1fr))`,
            gap: "8px",
          }}
        >
          {images.map((image, index) => {
            const isDragging = dragIndex === index;
            const isDropTarget = dragOverIndex === index;
            const isPrimary = image.isPrimary === true;

            return (
              <Box
                key={image.id}
                draggable
                onDragStart={(e) => handleReorderDragStart(e, index)}
                onDragOver={(e) => handleReorderDragOver(e, index)}
                onDrop={(e) => handleReorderDrop(e, index)}
                onDragEnd={handleReorderDragEnd}
                sx={{
                  position: "relative",
                  width: `${thumbnailSize}px`,
                  height: `${thumbnailSize}px`,
                  borderRadius: "4px",
                  overflow: "hidden",
                  border: isPrimary
                    ? `2px solid ${dt.blue}`
                    : isDropTarget
                    ? `2px solid ${dt.blue}`
                    : `1px solid ${dt.cardBorder}`,
                  opacity: isDragging ? 0.4 : 1,
                  cursor: "grab",
                  transition: "all 0.15s ease",
                  flexShrink: 0,
                  "&:hover .thumbnail-overlay": {
                    opacity: 1,
                  },
                  "&:active": {
                    cursor: "grabbing",
                  },
                }}
                role="listitem"
                aria-label={`${image.name || `Image ${index + 1}`}${
                  isPrimary ? ` (${translate("gallery.upload.setPrimary")})` : ""
                }`}
              >
                {/* Image */}
                <img
                  src={image.thumbnailSrc || image.src}
                  alt={image.alt || image.name || `Image ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  onClick={() => handleThumbnailClick(index)}
                />

                {/* Hover overlay */}
                <Box
                  className="thumbnail-overlay"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.45)",
                    opacity: 0,
                    transition: "opacity 0.15s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DragIndicatorIcon
                    sx={{ fontSize: 20, color: "#FFFFFF", opacity: 0.7 }}
                  />
                </Box>

                {/* Primary star - top left */}
                <Tooltip
                  title={
                    isPrimary
                      ? translate("gallery.upload.removePrimary")
                      : translate("gallery.upload.setPrimary")
                  }
                  enterDelay={300}
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePrimary(index);
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      left: 2,
                      padding: "2px",
                      backgroundColor: "rgba(0,0,0,0.35)",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.55)",
                      },
                    }}
                    aria-label={
                      isPrimary
                        ? translate("gallery.upload.removePrimary")
                        : translate("gallery.upload.setPrimary")
                    }
                  >
                    {isPrimary ? (
                      <StarIcon
                        sx={{ fontSize: compact ? 14 : 16, color: "#F59E0B" }}
                      />
                    ) : (
                      <StarOutlineIcon
                        sx={{
                          fontSize: compact ? 14 : 16,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Remove button - top right */}
                <Tooltip
                  title={translate("gallery.upload.remove")}
                  enterDelay={300}
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(index);
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      padding: "2px",
                      backgroundColor: "rgba(0,0,0,0.35)",
                      "&:hover": {
                        backgroundColor: "rgba(220,38,38,0.8)",
                      },
                    }}
                    aria-label={translate("gallery.upload.remove")}
                  >
                    <CloseIcon
                      sx={{
                        fontSize: compact ? 12 : 14,
                        color: "#FFFFFF",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Counter */}
      <Box
        sx={{
          mt: 1,
          fontSize: "12px",
          fontFamily: dt.fontMono,
          fontWeight: 600,
          color: dt.textMuted,
          letterSpacing: "0.3px",
        }}
      >
        {translate("gallery.upload.maxImages", {
          current: images.length,
          max: maxImages,
        })}
      </Box>

      {/* Crop dialog */}
      {enableCrop && cropImageIndex !== null && images[cropImageIndex] && (
        <UploadImageCrop
          open={true}
          onClose={handleCropClose}
          file={{
            name: images[cropImageIndex].name,
            content: images[cropImageIndex].src,
          }}
          onChangeFile={handleCropSave}
          cropVariant={cropVariant}
          maxFileSizeMb={maxFileSizeMb}
          minCropWidth={100}
          modalWidth="600px"
          aspectRatio={cropAspectRatio}
        />
      )}
    </Box>
  );
};

export default MultiImageUpload;
